import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Eye, EyeSlash, CaretDown, CaretRight, Bell, Info, ArrowUpRight, ArrowDown,
  CheckCircle, WarningCircle, Clock, Minus, ArrowRight, House, Receipt,
} from "@phosphor-icons/react";
import { ResponsiveContainer, ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip } from "recharts";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { CapexTooltipContent } from "@/components/CapexTooltipContent";
import { useHousehold } from "@/context/HouseholdContext";
import { useDemo } from "@/context/DemoContext";
import { useNotesRollup } from "@/hooks/useNotesRollup";
import { ASSET_TYPE_LABELS, holdingStatus } from "@/lib/api";
import { attentionReasons } from "@/lib/attention";

const STATUS_STYLES = {
  success:     { bg: "bg-[hsl(var(--ploy-pos-soft))]",     text: "text-[hsl(var(--ploy-pos))]",     icon: CheckCircle },
  warning:     { bg: "bg-[hsl(var(--ploy-attn-soft))]",    text: "text-[hsl(var(--ploy-attn))]",    icon: WarningCircle },
  destructive: { bg: "bg-[hsl(var(--ploy-rust-soft))]",    text: "text-[hsl(var(--ploy-rust))]",    icon: Clock },
  muted:       { bg: "bg-[hsl(var(--ploy-neutral-soft))]", text: "text-[hsl(var(--ploy-neutral))]", icon: Minus },
};
const StatusPill = ({ status, testid }) => {
  const s = STATUS_STYLES[status.tone] || STATUS_STYLES.muted;
  const Icon = s.icon;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.bg} ${s.text}`}>
      <Icon size={11} weight="fill" /> {status.label}
    </span>
  );
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const distMonth = (d) => {
  const cd = (d?.check_date || "").slice(0, 7);
  if (/^\d{4}-\d{2}$/.test(cd)) return cd;
  const m = /^(\d{2})\/(\d{4})$/.exec(d?.period_max || "");
  return m ? `${m[2]}-${m[1]}` : "";
};
const distVal = (d) => Number(d?.owner_net) || Number(d?.paid_total) || 0;
const SOURCE_LABELS = { fci: "FCI", allied: "Allied", self: "Self-Service" };
const wholeDollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const fmtWholeDollars = (n) => wholeDollarFormatter.format(Number(n) || 0);
const formatMonthLabel = (mk) => {
  const [, m] = String(mk || "").split("-");
  const monthIndex = Number(m) - 1;
  return MONTHS[monthIndex] || "";
};
const keyOf = (value) => {
  if (value === null || value === undefined || typeof value === "object") return "";
  return String(value).trim().toLowerCase();
};
const textOrDash = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "object") return "—";
  return String(value);
};
const initialsFromLabel = (label) => String(label || "").trim().split(/\s+/).filter(Boolean)
  .slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
const labelize = (value) => keyOf(value).split("_").filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
const isFormerHolding = (a) => keyOf(a?.ownership_status) === "former";
const isOwnedHolding = (a) => !isFormerHolding(a);
const noteDaysLate = (note) => {
  const days = Number(note?.computed_days_late ?? 0);
  return Number.isFinite(days) ? Math.max(0, Math.round(days)) : 0;
};
const fmtRate = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "object") return "—";
  const pct = Number(value) * 100;
  if (!Number.isFinite(pct)) return "—";
  return `${pct.toFixed(2)}%`;
};
const noteStatusFrom = (note, fallback) => {
  if (!note) return fallback;
  const status = keyOf(note.status);
  if (status === "paid_off") return { key: "paid_off", label: "Paid Off", tone: "muted" };
  const daysLate = noteDaysLate(note);
  if (daysLate > 0) {
    const attention = keyOf(note.attention);
    const tone = attention === "late_90" || attention === "past_maturity" || daysLate >= 90 ? "destructive" : "warning";
    return { key: attention || "late", label: `${daysLate}d late`, tone };
  }
  return { key: "performing", label: "Performing", tone: "muted" };
};
const fmtSyncedAt = (value) => {
  if (!value || typeof value === "object") return "sync pending";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "sync pending";
  const diff = Date.now() - date.getTime();
  if (diff < 0) return `synced ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const minutes = Math.max(1, Math.round(diff / 60000));
  if (minutes < 60) return `synced ${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `synced ${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `synced ${days}d ago`;
  return `synced ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

/**
 * Tabbed silo template (KPI cards act as tabs; selected one lights up + caret connector,
 * swapping the contextual detail panel below). Used by Real Estate and Mortgage Notes.
 *   mode: "real_estate" → 4th KPI = Vacancy
 *   mode: "notes"       → 4th KPI = Performing, value label = Unpaid Balance
 */
export default function TabbedSilo({ eyebrow, title, subtitle, assetType, mode = "real_estate" }) {
  const navigate = useNavigate();
  const { fmtAsset } = useDemo();
  const { assets, entities, liabilities, ownerLabels, ownerLabel,
          assetProblemIds, liabilityDueIds, hid } = useHousehold();

  const isNotes = mode === "notes";
  const valueLabel = isNotes ? "Unpaid Balance" : "Portfolio Value";
  const IconLg = isNotes ? Receipt : House;

  const [ownerFilter, setOwnerFilter] = useState("both");
  const [entityFilter, setEntityFilter] = useState("all");
  const [ownershipFilter, setOwnershipFilter] = useState("owned");
  const [privacy, setPrivacy] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [activeKpi, setActiveKpi] = useState("value");   // value | cashflow | yield | vacancy|performing | attention
  const [includeCapex, setIncludeCapex] = useState(false);
  const [loadedIncludeCapex, setLoadedIncludeCapex] = useState(false);
  const [capexPending, setCapexPending] = useState(false);
  const [capexRefetchError, setCapexRefetchError] = useState(false);
  const initialHistoryAttemptedRef = useRef(false);
  const notesRollup = useNotesRollup(hid, isNotes && !!hid);

  // Real per-month net history from filed transactions (real-estate only).
  const [reHistory, setReHistory] = useState(null); // { has_data, months:[{month,net}] } | null
  useEffect(() => {
    setReHistory(null);
    setIncludeCapex(false);
    setLoadedIncludeCapex(false);
    setCapexPending(false);
    setCapexRefetchError(false);
    initialHistoryAttemptedRef.current = false;
  }, [hid, mode]);

  useEffect(() => {
    if (mode !== "real_estate" || !hid) { setReHistory(null); return; }
    if (reHistory && includeCapex === loadedIncludeCapex) return;
    if (!reHistory && initialHistoryAttemptedRef.current && includeCapex === loadedIncludeCapex) return;

    let cancelled = false;
    const isToggleRefetch = reHistory !== null;
    initialHistoryAttemptedRef.current = true;
    if (isToggleRefetch) setCapexPending(true);

    const url = `/households/${hid}/re-cashflow-history?months=12${includeCapex ? "&include_capex=true" : ""}`;
    api.get(url)
      .then((r) => {
        if (cancelled) return;
        setReHistory(r.data);
        setLoadedIncludeCapex(includeCapex);
        setCapexRefetchError(false);
        setCapexPending(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIncludeCapex(loadedIncludeCapex);
        setCapexRefetchError(true);
        setCapexPending(false);
      });
    return () => {
      cancelled = true;
      setCapexPending(false);
    };
  }, [mode, hid, includeCapex, loadedIncludeCapex, reHistory]);

  const filtered = useMemo(() => {
    let list = (assets || []).filter((a) => a.asset_type === assetType);
    if (ownerFilter !== "both") list = list.filter((a) => a.owner_party === ownerFilter || a.owner_party === "joint");
    if (!isNotes && entityFilter !== "all") list = list.filter((a) => a.entity_id === entityFilter);
    return list;
  }, [assets, assetType, ownerFilter, entityFilter, isNotes]);
  const metricHoldings = useMemo(() => (
    isNotes ? filtered : filtered.filter(isOwnedHolding)
  ), [filtered, isNotes]);
  const tableHoldings = useMemo(() => {
    if (isNotes) return filtered;
    return filtered.filter((a) => ownershipFilter === "former" ? isFormerHolding(a) : isOwnedHolding(a));
  }, [filtered, isNotes, ownershipFilter]);

  const entityName = (id) => entities?.find((e) => e.id === id)?.name || "—";

  const value = (a) => Number(a.current_value || 0);
  const assetMonthly = (a) => {
    if (a.asset_type === "real_estate" && Array.isArray(a.units) && a.units.length)
      return a.units.reduce((s, u) => s + Number(u.monthly_rent || 0), 0);
    if (Number(a.meta?.monthly_payment || 0) > 0) return Number(a.meta.monthly_payment);
    if (Number(a.meta?.monthly_income || 0) > 0) return Number(a.meta.monthly_income);
    const dists = a.meta?.distributions;
    if (Array.isArray(dists) && dists.length) {
      const paying = dists.filter((d) => distVal(d) > 0);
      const pool = paying.length ? paying : dists;
      const latest = [...pool].sort((x, y) => distMonth(y).localeCompare(distMonth(x)))[0];
      return distVal(latest);
    }
    return 0;
  };
  const carryOf = (a) => Number(a.meta?.monthly_carry || 0);
  const yieldOf = (a) => { const v = value(a), m = assetMonthly(a); return v > 0 && m > 0 ? (m * 12 / v) * 100 : null; };
  const noteFor = (a) => a?.note_loan_id ? notesRollup.byLoanId.get(a.note_loan_id) || notesRollup.byLoanId.get(String(a.note_loan_id)) : null;
  const noteRateOf = (a) => {
    const note = noteFor(a);
    return note?.note_rate ?? a?.meta?.note_rate ?? null;
  };

  const totalValue   = metricHoldings.reduce((s, a) => s + value(a), 0);
  const monthlyIncome = metricHoldings.reduce((s, a) => s + assetMonthly(a), 0);
  const monthlyCarry  = metricHoldings.reduce((s, a) => s + carryOf(a), 0);
  const netMonthly    = monthlyIncome - monthlyCarry;
  const incomeProducing = metricHoldings.filter((a) => assetMonthly(a) > 0).length;
  const blendedYield  = totalValue > 0 && monthlyIncome > 0 ? ((monthlyIncome * 12) / totalValue) * 100 : 0;
  const blendedRate = Number(notesRollup.rollup?.weighted_avg_rate);

  // occupancy (real estate)
  const occ = useMemo(() => {
    let total = 0, occupied = 0; const vacantUnits = [];
    metricHoldings.forEach((a) => {
      (Array.isArray(a.units) ? a.units : []).forEach((u) => {
        total += 1;
        if (Number(u.monthly_rent || 0) > 0) occupied += 1;
        else vacantUnits.push({ asset: a, unit: u });
      });
    });
    return { total, occupied, vacant: total - occupied, pct: total > 0 ? (occupied / total) * 100 : 0, vacantUnits };
  }, [metricHoldings]);

  const isNotePerforming = (a) => {
    const note = noteFor(a);
    return !!note && noteDaysLate(note) === 0 && keyOf(note.status) !== "paid_off";
  };
  const performingList = isNotes ? metricHoldings.filter(isNotePerforming) : metricHoldings.filter((a) => assetMonthly(a) > 0);
  const watchList = isNotes ? metricHoldings.filter((a) => !isNotePerforming(a)) : metricHoldings.filter((a) => assetMonthly(a) <= 0);

  const attentionReasonsFor = (a) => attentionReasons(a, { isNotes, note: noteFor(a) });
  const attentionList = metricHoldings.filter((a) => attentionReasonsFor(a).length > 0);

  const statusFor = (a) => {
    const isLate = (liabilities || []).some((l) => l.asset_id === a.id && liabilityDueIds?.has(l.id));
    const fallback = holdingStatus(a, { hasIncome: assetMonthly(a) > 0, isLate, hasAlert: assetProblemIds?.has(a.id) });
    return isNotes ? noteStatusFrom(noteFor(a), fallback) : fallback;
  };
  const sourceFreshness = useMemo(() => {
    if (!isNotes) return [];
    const bySource = new Map();
    filtered.forEach((a) => {
      const note = a?.note_loan_id ? notesRollup.byLoanId.get(a.note_loan_id) || notesRollup.byLoanId.get(String(a.note_loan_id)) : null;
      const source = keyOf(note?.source || note?.last_synced_source);
      if (!source) return;
      const syncedAt = note?.last_synced_at || null;
      const syncedMs = syncedAt && typeof syncedAt !== "object" ? new Date(syncedAt).getTime() : 0;
      const prior = bySource.get(source);
      if (!prior || syncedMs > prior.syncedMs) {
        bySource.set(source, {
          key: source,
          label: SOURCE_LABELS[source] || labelize(source) || "Source",
          syncedAt,
          syncedMs: Number.isFinite(syncedMs) ? syncedMs : 0,
        });
      }
    });
    return Array.from(bySource.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filtered, isNotes, notesRollup.byLoanId]);

  // trailing 12-mo net-income series.
  // Real estate → real per-month net from filed transactions (db.transactions).
  // Notes / no-data → distributions when present, else flat recurring fallback.
  const trailing = useMemo(() => {
    const now = new Date();
    const labelFor = (mk) => {
      const [y, m] = (mk || "").split("-").map(Number);
      return MONTHS[(m || 1) - 1];
    };
    // Real-estate path: use server-aggregated transaction history when it has data.
    if (mode === "real_estate" && reHistory?.has_data && Array.isArray(reHistory.months)) {
      return reHistory.months.map((row, idx) => ({
        month: labelFor(row.month),
        value: Number(row.net) || 0,
        prior: row.prior_net || 0,
        capex: Number(row.capex) || 0,
        isCurrent: idx === reHistory.months.length - 1,
      }));
    }
    const buckets = {};
    metricHoldings.forEach((a) => {
      const dists = Array.isArray(a.meta?.distributions) ? a.meta.distributions : [];
      const seen = new Set();
      dists.forEach((d) => {
        const mk = distMonth(d); if (!mk) return;
        const k = `${a.id}|${mk}|${d.check_number || ""}|${distVal(d)}`;
        if (seen.has(k)) return; seen.add(k);
        buckets[mk] = (buckets[mk] || 0) + distVal(d);
      });
    });
    const hasData = Object.keys(buckets).length > 0;
    const arr = [];
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mk = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const py = new Date(dt.getFullYear() - 1, dt.getMonth(), 1);
      const pyk = `${py.getFullYear()}-${String(py.getMonth() + 1).padStart(2, "0")}`;
      const v = hasData ? (buckets[mk] || 0) : netMonthly;
      arr.push({ month: MONTHS[dt.getMonth()], value: Math.max(0, v), prior: buckets[pyk] || 0, isCurrent: i === 0 });
    }
    return arr;
  }, [mode, reHistory, metricHoldings, netMonthly]);
  const trailingAvg = (n) => { const s = trailing.slice(12 - n); return s.length ? s.reduce((x, p) => x + p.value, 0) / s.length : 0; };
  const hasPrior = trailing.some((p) => p.prior !== 0);
  // Current Net Monthly excludes former holdings; historical bars may still use server history.
  const realNetMonthly = netMonthly;

  const fmt = (n) => (privacy ? "•••••" : fmtAsset(n));
  const fmtK = (n) => {
    if (privacy) return "•••";
    const abs = Math.abs(Number(n) || 0);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return fmtAsset(n);
  };
  const fmtFull = (n) => (privacy ? "•••" : fmtWholeDollars(n));
  const monthTag = `${MONTHS[new Date().getMonth()]} ${new Date().getFullYear()}`.toUpperCase();
  const coverageCaveat = useMemo(() => {
    if (!reHistory || reHistory.has_data === false) {
      return { show: false, pct: null, direction: null, note: "" };
    }
    const rawCoverage = reHistory.coverage;
    if (rawCoverage === null || rawCoverage === undefined) {
      return { show: false, pct: null, direction: null, note: "" };
    }
    const coverage = Number(rawCoverage);
    if (!Number.isFinite(coverage) || coverage >= 0.98) {
      return { show: false, pct: null, direction: null, note: "" };
    }
    const uncategorizedNet = Number(reHistory.uncategorized_net);
    const direction = uncategorizedNet < 0 ? "overstate" : uncategorizedNet > 0 ? "understate" : null;
    const pct = Math.round(coverage * 100);
    const count = Number(reHistory.uncategorized_count);
    const amount = fmt(Math.abs(uncategorizedNet));
    const note = direction
      ? `Based on ${pct}% of filed activity. ${count} unclassified items totalling ${amount} are excluded, so this figure may ${direction} actual net.`
      : `Based on ${pct}% of filed activity. ${count} unclassified items totalling ${amount} are excluded from this figure.`;
    return { show: true, pct, direction, note };
  }, [fmt, reHistory]);
  const capexAnnotation = useMemo(() => {
    if (mode !== "real_estate" || !reHistory?.has_data || !Array.isArray(reHistory.months)) return null;
    const capexMonths = reHistory.months
      .map((row) => ({ month: formatMonthLabel(row.month), capex: Number(row.capex) || 0 }))
      .filter((row) => row.capex > 0);
    if (capexMonths.length === 0) return null;

    const modeText = loadedIncludeCapex ? "included in Net Monthly" : "excluded from Net Monthly";
    if (capexMonths.length <= 3) {
      return {
        lines: capexMonths.map((row) => `${row.month} had ${fmt(row.capex)} in capital improvements — ${modeText}.`),
      };
    }
    const totalCapex = capexMonths.reduce((sum, row) => sum + row.capex, 0);
    return {
      lines: [`${capexMonths.length} months had capital improvements totalling ${fmt(totalCapex)} — hover the bars for detail.`],
    };
  }, [fmt, loadedIncludeCapex, mode, reHistory]);
  const ctx = {
    fmt, fmtK, fmtFull, privacy, statusFor, ownerLabel, entityName, value, assetMonthly, yieldOf, isNotes,
    noteRateOf, fmtRate, sourceFreshness, notesLoading: notesRollup.loading, notesError: notesRollup.error,
  };

  return (
    <div className="space-y-7 animate-fade-up" data-testid={`tabbed-silo-${assetType}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap pb-5 border-b border-border">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[hsl(var(--ploy-brass-soft))] text-[hsl(var(--accent))] flex items-center justify-center shrink-0" data-testid="silo-icon-box">
            <IconLg size={24} weight="regular" />
          </div>
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1 className="page-title text-[40px] sm:text-[48px] mt-1 leading-none" data-testid="page-title">{title}</h1>
            <p className="text-sm text-muted-foreground mt-2.5" data-testid="silo-holdings-count">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full bg-[hsl(var(--ploy-input-well))] p-0.5">
            {[{ k: "both", l: "Both" }, { k: "A", l: ownerLabels?.A || "A" }, { k: "B", l: ownerLabels?.B || "B" }].map((o) => (
              <button key={o.k} data-testid={`owner-filter-${o.k}`} onClick={() => setOwnerFilter(o.k)}
                className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${ownerFilter === o.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}>
                {o.l}
              </button>
            ))}
          </div>
          {!isNotes && (
            <div className="inline-flex rounded-full bg-[hsl(var(--ploy-input-well))] p-0.5">
              {[{ k: "owned", l: "Owned" }, { k: "former", l: "Previously owned" }].map((o) => (
                <button key={o.k} data-testid={`ownership-filter-${o.k}`} onClick={() => setOwnershipFilter(o.k)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${ownershipFilter === o.k ? "bg-foreground text-background" : "text-foreground/70 hover:text-foreground"}`}>
                  {o.l}
                </button>
              ))}
            </div>
          )}
          {!isNotes && (
            <div className="relative">
              <select data-testid="entity-filter" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-1.5 text-xs font-semibold rounded-full bg-[hsl(var(--ploy-input-well))] hover:bg-[hsl(var(--ploy-border))] text-foreground border border-transparent focus:outline-none cursor-pointer">
                <option value="all">All entities</option>
                {(entities || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <CaretDown size={11} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
            </div>
          )}
          <button data-testid="privacy-toggle" aria-label={privacy ? "Hide amounts" : "Privacy"} onClick={() => setPrivacy((p) => !p)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
              privacy ? "bg-foreground text-background border-foreground" : "bg-transparent border-[hsl(var(--ploy-input-border))] text-foreground/80 hover:bg-[hsl(var(--ploy-input-well))]"
            }`}>
            {privacy ? <EyeSlash size={12} /> : <Eye size={12} />} Privacy
          </button>
        </div>
      </div>

      {/* KPI tab strip — 5 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <KpiCard kpi="value" active={activeKpi} onSelect={setActiveKpi} testid="kpi-value" label={valueLabel}>
          <p className="hero-metric text-3xl mt-2 text-foreground">{fmtK(totalValue)}</p>
          <p className="text-[11px] text-muted-foreground mt-1.5">{metricHoldings.length} {metricHoldings.length === 1 ? "holding" : "holdings"}</p>
        </KpiCard>

        <KpiCard kpi="cashflow" active={activeKpi} onSelect={setActiveKpi} testid="kpi-net-monthly" label="Net Monthly">
          <p className={`hero-metric text-3xl mt-2 ${realNetMonthly < 0 ? "text-[hsl(var(--ploy-rust))]" : "text-foreground"}`}>{fmt(realNetMonthly)}</p>
          <p className="text-[11px] text-muted-foreground mt-1.5">Income less carrying cost</p>
          {coverageCaveat.show && (
            <span
              data-testid="kpi-net-monthly-coverage"
              title={coverageCaveat.note}
              className="inline-flex mt-2 rounded-full bg-[hsl(var(--ploy-attn-soft))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--ploy-attn))]"
            >
              {coverageCaveat.pct}% classified
            </span>
          )}
          <p className="text-[10px] font-semibold tracking-[0.12em] text-foreground/60 mt-2">{monthTag}</p>
        </KpiCard>

        <KpiCard kpi="yield" active={activeKpi} onSelect={setActiveKpi} testid="kpi-blended-yield" label={isNotes ? "Blended Rate" : "Blended Yield"}>
          <p className="hero-metric text-3xl mt-2 text-foreground">{privacy ? "•••" : isNotes ? (Number.isFinite(blendedRate) ? `${(blendedRate * 100).toFixed(2)}%` : "—") : `${blendedYield.toFixed(1)}%`}</p>
          <p className="text-[11px] text-muted-foreground mt-1.5">{isNotes ? "Weighted by UPB" : "On income-producing"}</p>
        </KpiCard>

        {isNotes ? (
          <KpiCard kpi="performing" active={activeKpi} onSelect={setActiveKpi} testid="kpi-performing" label="Performing">
            <p className="hero-metric text-3xl mt-2 text-foreground">{performingList.length}/{metricHoldings.length || 0}</p>
            <p className="text-[11px] text-muted-foreground mt-1.5">Current by servicer</p>
          </KpiCard>
        ) : (
          <KpiCard kpi="vacancy" active={activeKpi} onSelect={setActiveKpi} testid="kpi-vacancy" label="Vacancy">
            <p className={`hero-metric text-3xl mt-2 ${occ.vacant > 0 ? "text-[hsl(var(--ploy-attn))]" : "text-foreground"}`}>
              {occ.total > 0 ? occ.vacant : "—"}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1.5">{occ.total > 0 ? `of ${occ.total} units · ${occ.pct.toFixed(0)}% occupied` : "No unit data yet"}</p>
          </KpiCard>
        )}

        <KpiCard kpi="attention" active={activeKpi} onSelect={setActiveKpi} testid="kpi-needs-attention" label="Needs Attention">
          <p className={`hero-metric text-3xl mt-2 ${attentionList.length > 0 ? "text-[hsl(var(--ploy-attn))]" : "text-foreground"}`}>{attentionList.length}</p>
          {attentionList.length > 0 ? (
            <p className="text-[11px] text-[hsl(var(--ploy-attn))] mt-1.5 flex items-center gap-1"><Bell size={11} weight="fill" /> Flagged below</p>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1.5">All clear</p>
          )}
        </KpiCard>
      </div>

      {/* Contextual detail panel */}
      <div className="bg-card rounded-2xl border-2 border-[hsl(var(--accent)/0.55)] overflow-hidden" data-testid="silo-detail-panel">
        {activeKpi === "value" && <HoldingsPanel filtered={tableHoldings} ctx={ctx} title={title} onPick={setSelectedAsset} ownershipFilter={isNotes ? null : ownershipFilter} />}
        {activeKpi === "cashflow" && (
          <CashflowPanel
            trailing={trailing}
            netMonthly={netMonthly}
            t3={trailingAvg(3)}
            t6={trailingAvg(6)}
            t12={trailingAvg(12)}
            fmt={fmt}
            privacy={privacy}
            hasPrior={hasPrior}
            coverageCaveat={coverageCaveat}
            showCapexControls={!isNotes}
            includeCapex={includeCapex}
            loadedIncludeCapex={loadedIncludeCapex}
            onIncludeCapexChange={setIncludeCapex}
            capexPending={capexPending}
            capexRefetchError={capexRefetchError}
            capexAnnotation={capexAnnotation}
          />
        )}
        {activeKpi === "yield" && <YieldPanel filtered={metricHoldings} ctx={ctx} onPick={setSelectedAsset} />}
        {activeKpi === "vacancy" && <VacancyPanel occ={occ} filtered={metricHoldings} ctx={ctx} onPick={setSelectedAsset} />}
        {activeKpi === "performing" && <PerformingPanel performingList={performingList} watchList={watchList} ctx={ctx} onPick={setSelectedAsset} />}
        {activeKpi === "attention" && <AttentionPanel attentionList={attentionList} statusFor={statusFor} attentionReasonsFor={attentionReasonsFor} onPick={setSelectedAsset} />}
      </div>

      <HoldingDrawer asset={selectedAsset} onClose={() => setSelectedAsset(null)}
        onOpenFull={(a) => { setSelectedAsset(null); navigate(a.asset_type === "real_estate" ? `/properties/${a.id}` : `/assets/${a.id}`); }} ctx={ctx} />
    </div>
  );
}

const KpiCard = ({ kpi, active, onSelect, testid, label, children }) => {
  const selected = active === kpi;
  return (
    <div data-testid={testid} role="button" tabIndex={0}
      onClick={() => onSelect(kpi)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(kpi); } }}
      className={`relative bg-card rounded-2xl px-5 py-5 border cursor-pointer transition-all ${
        selected ? "border-[hsl(var(--accent))] ring-1 ring-[hsl(var(--accent))] shadow-[0_4px_18px_hsl(var(--ploy-ink)/0.08)]"
                 : "border-border hover:border-[hsl(var(--ploy-input-border))]"
      }`}>
      <p className="eyebrow flex items-center justify-between gap-1">{label}
        <CaretDown size={11} className={`transition-transform ${selected ? "rotate-180 text-[hsl(var(--accent))]" : "opacity-40"}`} />
      </p>
      {children}
      {selected && <span data-testid={`${testid}-caret`} className="absolute -bottom-[10px] left-1/2 -translate-x-1/2 h-4 w-4 rotate-45 bg-card border-b border-r border-[hsl(var(--accent))] z-10" />}
    </div>
  );
};

// ── Value tab → holdings table ──
const HoldingsPanel = ({ filtered, ctx, title, onPick, ownershipFilter }) => {
  const { fmt, fmtK, fmtFull, statusFor, ownerLabel, value, assetMonthly, yieldOf, noteRateOf, fmtRate, isNotes } = ctx;
  if (filtered.length === 0) {
    if (ownershipFilter === "former") {
      return (
        <div className="py-14 text-center" data-testid="former-empty-state">
          <p className="font-serif text-xl">No previously owned holdings</p>
          <p className="text-sm text-muted-foreground mt-1">Properties no longer owned will appear here.</p>
        </div>
      );
    }
    return (
      <div className="py-14 text-center" data-testid="empty-state">
        <p className="font-serif text-xl">No {title.toLowerCase()} yet</p>
        <p className="text-sm text-muted-foreground mt-1">Add a holding to begin tracking it here.</p>
      </div>
    );
  }
  return (
    <div data-testid="holdings-panel">
      {isNotes && <NotesSourceFreshness ctx={ctx} />}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground bg-muted">
            <th className="text-left px-5 py-3">Holding</th>
            <th className="text-left px-3 py-3">Status</th>
            <th className="text-left px-3 py-3">Owner</th>
            <th className="text-right px-3 py-3">{isNotes ? "UPB" : "Value"}</th>
            <th className="text-right px-3 py-3">Monthly</th>
            <th className="text-right px-3 py-3">{isNotes ? "Rate" : "Yield"}</th>
            <th className="w-6 px-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {filtered.map((a) => {
            const monthly = assetMonthly(a); const carry = Number(a.meta?.monthly_carry || 0); const y = yieldOf(a); const rate = noteRateOf(a);
            const ownerFull = ownerLabel(a.owner_party);
            const ownerDisplay = a.owner_party === "joint" ? "Joint" : initialsFromLabel(ownerFull);
            return (
              <tr key={a.id} data-testid={`holding-row-${a.id}`} onClick={() => onPick(a)}
                className="cursor-pointer hover:bg-[hsl(var(--ploy-row-hover))] transition-colors">
                <td className="px-5 py-3.5">
                  <p className="font-semibold text-foreground">{a.name}</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    {ASSET_TYPE_LABELS[a.asset_type] || a.asset_type}
                    {a.address_parts?.city && ` · ${a.address_parts.city}${a.address_parts.state ? ", " + a.address_parts.state : ""}`}
                  </p>
                </td>
                <td className="px-3 py-3.5"><StatusPill status={statusFor(a)} testid={`holding-status-${a.id}`} /></td>
                <td data-testid={`holding-owner-${a.id}`} title={ownerFull} className="px-3 py-3.5 text-foreground/80">{ownerDisplay}</td>
                <td className="px-3 py-3.5 text-right hero-metric">{isNotes ? fmtFull(value(a)) : fmtK(value(a))}</td>
                <td className="px-3 py-3.5 text-right tabular-nums">
                  {monthly > 0 ? <span className="text-foreground">{fmt(monthly)}</span>
                    : carry > 0 ? <span className="text-[hsl(var(--ploy-rust))]">-{fmt(carry)}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-3.5 text-right tabular-nums">{isNotes ? fmtRate(rate) : y != null ? `${y.toFixed(1)}%` : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-2 py-3.5 text-muted-foreground"><CaretRight size={14} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-5 py-2.5 text-[11px] text-muted-foreground bg-muted border-t border-border flex items-center gap-1.5">
        <Info size={11} /> Synced from connected statements &amp; feeds.
      </div>
    </div>
  );
};

const NotesSourceFreshness = ({ ctx }) => {
  const { sourceFreshness, notesLoading, notesError } = ctx;
  const safeError = typeof notesError === "string" ? notesError : "";
  if (sourceFreshness.length === 0 && !notesLoading && !safeError) return null;
  return (
    <div className="px-5 py-2.5 text-[11px] text-muted-foreground bg-muted border-b border-border flex items-center gap-1.5 flex-wrap">
      <Info size={11} />
      {sourceFreshness.map((source) => (
        <span key={source.key}>{source.label} {fmtSyncedAt(source.syncedAt)}</span>
      ))}
      {sourceFreshness.length === 0 && notesLoading && <span>Loading note sync status...</span>}
      {sourceFreshness.length === 0 && safeError && <span>Note sync unavailable.</span>}
    </div>
  );
};

// ── Net Monthly tab → trend + trailing averages ──
const CashflowPanel = ({
  trailing, netMonthly, t3, t6, t12, fmt, privacy, hasPrior, coverageCaveat,
  showCapexControls, includeCapex, loadedIncludeCapex, onIncludeCapexChange, capexPending, capexRefetchError, capexAnnotation,
}) => {
  const latest = trailing[11]?.value ?? netMonthly;
  const prev = trailing[10]?.value ?? 0;
  const mom = prev > 0 ? ((latest - prev) / prev) * 100 : 0;
  const avgRow = (label, val, base, testid) => {
    const delta = base > 0 ? ((val - base) / base) * 100 : 0; const up = delta >= 0;
    return (
      <div className="flex items-center justify-between py-3.5 border-b border-border last:border-0" data-testid={testid}>
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-3">
          <span className="hero-metric text-xl text-foreground tabular-nums">{fmt(val)}</span>
          {base > 0 ? (
            <span className={`text-[12px] font-semibold inline-flex items-center gap-0.5 tabular-nums ${up ? "text-[hsl(var(--ploy-pos))]" : "text-[hsl(var(--ploy-attn))]"}`}>
              {up ? <ArrowUpRight size={11} /> : <ArrowDown size={11} />}{Math.abs(delta).toFixed(0)}%
            </span>
          ) : <span className="text-muted-foreground">—</span>}
        </div>
      </div>
    );
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-5 p-5 sm:p-6" data-testid="cashflow-panel">
      <div>
        <div className="flex items-center justify-between">
          <p className="eyebrow">Net Income · Trailing 12 mo{loadedIncludeCapex ? " · incl. capital expenses" : ""}</p>
          {mom !== 0 && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${mom >= 0 ? "text-[hsl(var(--ploy-pos))]" : "text-[hsl(var(--ploy-rust))]"}`}>
              <ArrowUpRight size={11} /> {mom >= 0 ? "+" : ""}{mom.toFixed(1)}% MoM
            </span>
          )}
        </div>
        <p className="hero-metric text-2xl mt-1 text-foreground">{fmt(latest)}</p>
        <div className="h-44 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trailing} margin={{ top: 8, right: 4, left: -10, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                cursor={{ fill: "hsl(var(--ploy-row-hover))" }}
                content={<CapexTooltipContent privacy={privacy} fmt={fmt} />}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={34} stroke="none">
                {trailing.map((p, i) => <Cell key={i} fill={p.isCurrent ? "hsl(var(--accent))" : "hsl(var(--ploy-brass)/0.45)"} />)}
              </Bar>
              {hasPrior && <Line type="monotone" dataKey="prior" stroke="hsl(var(--ploy-ink-4))" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {hasPrior && <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1.5"><span className="inline-block w-5 border-t border-dashed border-[hsl(var(--ploy-ink-4))]" /> Same month, prior year</p>}
        {(showCapexControls || coverageCaveat?.show || capexAnnotation || capexRefetchError) && (
          <div className="mt-3 space-y-2">
            {showCapexControls && (
              <div className="flex items-center justify-between gap-3 text-[11px] leading-5 text-muted-foreground">
                <div className="space-y-2">
                  {coverageCaveat?.show && (
                    <p data-testid="cashflow-coverage-note">
                      {coverageCaveat.note}
                    </p>
                  )}
                  {capexRefetchError && (
                    <p data-testid="capex-refetch-error">
                      Couldn't update — showing previous view
                    </p>
                  )}
                  {capexAnnotation && (
                    <div data-testid="capex-annotation">
                      {capexAnnotation.lines.map((line) => <p key={line}>{line}</p>)}
                    </div>
                  )}
                </div>
                <label className="inline-flex shrink-0 items-center gap-2 text-right font-medium text-foreground">
                  <span>Include capital expenses</span>
                  <Switch
                    data-testid="capex-toggle"
                    checked={includeCapex}
                    onCheckedChange={onIncludeCapexChange}
                    disabled={capexPending}
                    className="shrink-0"
                  />
                </label>
              </div>
            )}
            {!showCapexControls && coverageCaveat?.show && (
              <p data-testid="cashflow-coverage-note" className="text-[11px] leading-5 text-muted-foreground">
                {coverageCaveat.note}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="bg-background rounded-xl border border-border p-5">
        <p className="eyebrow mb-1">Trailing Averages</p>
        {avgRow("Trailing 3 mo", t3, t12, "avg-3mo")}
        {avgRow("Trailing 6 mo", t6, t12, "avg-6mo")}
        <div className="flex items-center justify-between py-3.5" data-testid="avg-12mo">
          <span className="text-sm text-muted-foreground">Trailing 12 mo</span>
          <span className="hero-metric text-xl text-foreground tabular-nums">{fmt(t12)}</span>
        </div>
      </div>
    </div>
  );
};

// ── Blended Yield tab → ranked per-holding yield list ──
const YieldPanel = ({ filtered, ctx, onPick }) => {
  const { fmtK, yieldOf, value, entityName, isNotes, noteRateOf } = ctx;
  const rows = filtered.map((a) => {
    const rate = noteRateOf(a);
    return { a, y: isNotes && rate != null ? Number(rate) * 100 : yieldOf(a) };
  }).sort((x, y) => (y.y ?? -1) - (x.y ?? -1));
  const maxY = Math.max(1, ...rows.map((r) => r.y ?? 0));
  return (
    <div className="p-5 sm:p-6" data-testid="yield-panel">
      <p className="eyebrow mb-3">{isNotes ? "Rate by Holding" : "Yield by Holding"}</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No holdings to rank.</p> : (
        <div className="divide-y divide-border">
          {rows.map(({ a, y }) => (
            <button key={a.id} data-testid={`yield-row-${a.id}`} onClick={() => onPick(a)}
              className="w-full text-left py-3 hover:bg-[hsl(var(--ploy-row-hover))] -mx-2 px-2 rounded-md transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><span className="font-semibold text-foreground">{a.name}</span>
                  <span className="text-[12px] text-muted-foreground ml-2">{entityName(a.entity_id)} · {fmtK(value(a))}</span></div>
                <span className="font-semibold tabular-nums text-foreground shrink-0">{y != null ? `${y.toFixed(isNotes ? 2 : 1)}%` : "—"}</span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--ploy-input-well))] overflow-hidden">
                <div className="h-full rounded-full bg-[hsl(var(--accent))]" style={{ width: `${y != null ? Math.min(100, (y / maxY) * 100) : 0}%` }} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Vacancy tab (real estate) ──
const VacancyPanel = ({ occ, filtered, ctx, onPick }) => {
  const { entityName } = ctx;
  const Stat = ({ label, value, tone, testid }) => (
    <div className="bg-background rounded-xl border border-border p-4" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className={`hero-metric text-2xl mt-1.5 ${tone || "text-foreground"}`}>{value}</p>
    </div>
  );
  if (occ.total === 0) {
    return (
      <div className="p-8 text-center" data-testid="vacancy-panel">
        <House size={26} className="mx-auto text-muted-foreground opacity-50" />
        <p className="font-serif text-lg mt-2">No unit data yet</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Add units to a property to track occupancy and vacancy here.</p>
      </div>
    );
  }
  return (
    <div className="p-5 sm:p-6 space-y-5" data-testid="vacancy-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Total units" value={occ.total} testid="vac-total" />
        <Stat label="Occupied" value={occ.occupied} tone="text-[hsl(var(--ploy-pos))]" testid="vac-occupied" />
        <Stat label="Vacant" value={occ.vacant} tone={occ.vacant > 0 ? "text-[hsl(var(--ploy-attn))]" : "text-foreground"} testid="vac-vacant" />
        <Stat label="Occupancy" value={`${occ.pct.toFixed(0)}%`} testid="vac-pct" />
      </div>
      {occ.vacant > 0 && (
        <div>
          <p className="eyebrow mb-2">Vacant units</p>
          <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
            {occ.vacantUnits.map(({ asset, unit }, i) => (
              <button key={i} data-testid={`vacant-row-${i}`} onClick={() => onPick(asset)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--ploy-row-hover))] transition-colors">
                <div><p className="font-semibold text-foreground">{asset.name}{unit.label ? ` · ${unit.label}` : ""}</p>
                  <p className="text-[12px] text-muted-foreground">{entityName(asset.entity_id)}</p></div>
                <StatusPill status={{ tone: "warning", label: "Vacant" }} testid={`vacant-pill-${i}`} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Performing tab (notes) ──
const PerformingPanel = ({ performingList, watchList, ctx, onPick }) => {
  const { fmt, assetMonthly, entityName } = ctx;
  const Group = ({ label, items, tone, testid }) => (
    <div data-testid={testid}>
      <div className="flex items-center justify-between pb-2.5 mb-1 border-b border-border">
        <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]" style={{ color: `hsl(var(${tone}))` }}>
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: `hsl(var(${tone}))` }} />{label}</span>
        <span className="text-[12px] text-muted-foreground">{items.length}</span>
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground py-3">None.</p> : (
        <div className="divide-y divide-border/70">
          {items.map((a) => (
            <button key={a.id} data-testid={`perf-row-${a.id}`} onClick={() => onPick(a)}
              className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-[hsl(var(--ploy-row-hover))] -mx-2 px-2 rounded-md transition-colors">
              <div><span className="font-semibold text-foreground">{a.name}</span>
                <span className="text-[12px] text-muted-foreground ml-2">{entityName(a.entity_id)}</span></div>
              <span className="tabular-nums text-foreground">{assetMonthly(a) > 0 ? `${fmt(assetMonthly(a))}/mo` : "—"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="p-5 sm:p-6 space-y-6" data-testid="performing-panel">
      <Group label="Performing" items={performingList} tone="--ploy-pos" testid="perf-group" />
      <Group label="Watch / Review" items={watchList} tone="--ploy-attn" testid="watch-group" />
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Info size={11} /> Performing uses servicer days-late status when available.</p>
    </div>
  );
};

// ── Needs Attention tab ──
const AttentionPanel = ({ attentionList, statusFor, attentionReasonsFor, onPick }) => (
  <div className="p-5 sm:p-6" data-testid="attention-panel">
    <p className="eyebrow mb-3">Action Items</p>
    {attentionList.length === 0 ? (
      <div className="py-10 text-center">
        <CheckCircle size={26} weight="fill" className="mx-auto text-[hsl(var(--ploy-pos))]" />
        <p className="font-serif text-lg mt-2">Everything's in order</p>
        <p className="text-sm text-muted-foreground mt-1">Nothing needs attention right now.</p>
      </div>
    ) : (
      <div className="divide-y divide-border">
        {attentionList.map((a) => (
          <button key={a.id} data-testid={`attention-row-${a.id}`} onClick={() => onPick(a)}
            className="w-full flex items-center justify-between gap-3 py-3.5 text-left hover:bg-[hsl(var(--ploy-row-hover))] -mx-2 px-2 rounded-md transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <StatusPill status={statusFor(a)} testid={`attention-status-${a.id}`} />
              <div className="min-w-0"><p className="font-semibold text-foreground truncate">{a.name}</p>
                <p data-testid={`attention-reasons-${a.id}`} className="text-[12px] text-muted-foreground truncate">
                  {attentionReasonsFor(a).map((r) => r.label).join(" · ")}
                </p></div>
            </div>
            <CaretRight size={14} className="text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    )}
  </div>
);

const HoldingDrawer = ({ asset, onClose, onOpenFull, ctx }) => {
  if (!asset) return null;
  const { fmt, fmtK, fmtFull, statusFor, ownerLabel, entityName, value, assetMonthly, yieldOf, isNotes, noteRateOf, fmtRate } = ctx;
  const status = statusFor(asset);
  const monthly = assetMonthly(asset); const y = yieldOf(asset); const m = asset.meta || {};
  const statedRate = fmtRate(noteRateOf(asset));
  const loc = asset.address_parts?.city ? `${asset.address_parts.city}${asset.address_parts.state ? ", " + asset.address_parts.state : ""}` : null;
  const units = Array.isArray(asset.units) ? asset.units : [];
  const modeFacts = isNotes
    ? [["Maturity", textOrDash(m.maturity_date)], ["Monthly payment", monthly > 0 ? fmt(monthly) : "—"],
       ["Stated Rate", statedRate], ["Yield", y != null ? `${y.toFixed(1)}%` : "—"]]
    : [["Units", units.length || "—"], ["Occupied", units.length ? `${units.filter((u) => Number(u.monthly_rent || 0) > 0).length}/${units.length}` : "—"],
       ["Monthly rent", monthly > 0 ? fmt(monthly) : "—"], ["Gross yield", y != null ? `${y.toFixed(1)}%` : "—"]];

  return (
    <Sheet open={!!asset} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" data-testid="holding-peek-drawer" className="w-[94vw] sm:max-w-[460px] p-0 flex flex-col bg-background border-l border-border">
        <SheetTitle className="sr-only">{asset.name} detail</SheetTitle>
        <SheetDescription className="sr-only">Holding detail for {asset.name}.</SheetDescription>
        <div className="px-6 pt-6 pb-5 border-b border-border">
          <StatusPill status={status} testid="peek-status-pill" />
          <h2 className="font-serif text-3xl font-medium tracking-tight mt-3 leading-tight pr-8" data-testid="peek-asset-name">{asset.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{ASSET_TYPE_LABELS[asset.asset_type] || asset.asset_type}{loc && ` · ${loc}`}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="grid grid-cols-2 gap-x-4 gap-y-4" data-testid="peek-facts-grid">
            <Fact label={isNotes ? "Unpaid balance" : "Value"} value={isNotes ? fmtFull(value(asset)) : fmtK(value(asset))} testid="peek-fact-value" />
            <Fact label="Owner" value={ownerLabel(asset.owner_party)} testid="peek-fact-owner" />
            <Fact label="Entity" value={entityName(asset.entity_id)} testid="peek-fact-entity" />
            <Fact label="Monthly" value={monthly > 0 ? fmt(monthly) : "—"} testid="peek-fact-monthly" />
          </div>
          <div data-testid="peek-mode-grid">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-3">{isNotes ? "Note detail" : "Property detail"}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
              {modeFacts.map(([label, val], i) => <Fact key={i} label={label} value={val} testid={`peek-mode-fact-${i}`} />)}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border bg-background">
          <button data-testid="peek-open-full-button" onClick={() => onOpenFull(asset)}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[0.625rem] bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity">
            Open full detail <ArrowRight size={15} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Fact = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
    <p className="text-sm text-foreground mt-1 tabular-nums">{value ?? "—"}</p>
  </div>
);
